import React from 'react';
import { CARD } from '../ui';
import { RoleBadge, CompanyTag, YouTag } from '../team/Badges';
import { DATA_HUE, TRACK, formatCount, percentChange } from './theme';

// Submissions per person, nested the way the org is: each supervisor heads a
// group and their inspectors sit under it, so an admin reads company -> manager
// -> inspector in one pass. Mirrors the grouping on the Team page.
const groupUsers = (entries) => {
  const byId = new Map(entries.map((e) => [e.user_id, e]));
  const inspectors = entries.filter((e) => e.role === 'inspector');
  const reportsFor = (id) => inspectors.filter((e) => e.supervisor_id === id);

  const managers = entries.filter((e) => e.role === 'manager');
  const admins = entries.filter((e) => e.role === 'admin');
  const supervisors = [...managers, ...admins.filter((a) => reportsFor(a.user_id).length > 0)];

  const groups = supervisors.map((supervisor) => ({
    key: `sup-${supervisor.user_id}`,
    supervisor,
    members: reportsFor(supervisor.user_id),
  }));

  const standalone = [
    ...entries.filter((e) => e.role === 'dev'),
    ...admins.filter((a) => reportsFor(a.user_id).length === 0),
  ];
  if (standalone.length) {
    groups.push({ key: 'staff', title: 'Staff', members: standalone });
  }

  const orphans = inspectors.filter((e) => !byId.has(e.supervisor_id));
  if (orphans.length) {
    groups.push({ key: 'unassigned', title: 'Unassigned inspectors', members: orphans });
  }
  return groups;
};

const groupTotal = (group) =>
  (group.supervisor ? group.supervisor.count : 0) + group.members.reduce((sum, m) => sum + m.count, 0);

const MemberRow = ({ entry, max, isSelf, showCompany }) => {
  const change = percentChange(entry.count, entry.previous_count);
  return (
    <li className="px-6 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="text-sm text-gray-200 truncate">{entry.email}</span>
          {isSelf && <YouTag />}
          {showCompany && <CompanyTag name={entry.company_name} />}
        </div>
        <div className="flex items-baseline gap-2 flex-shrink-0">
          {change !== null && (
            <span className={`text-xs ${change > 0 ? 'text-emerald-400' : change < 0 ? 'text-amber-400' : 'text-gray-500'}`}>
              {change > 0 ? '▲ +' : change < 0 ? '▼ ' : '± '}{change}%
            </span>
          )}
          <span className="text-sm font-semibold text-white tabular-nums">{formatCount(entry.count)}</span>
        </div>
      </div>
      <div className="mt-2 h-1.5 rounded-sm overflow-hidden" style={{ backgroundColor: TRACK }}>
        <div
          className="h-full"
          style={{
            width: `${max ? (entry.count / max) * 100 : 0}%`,
            backgroundColor: DATA_HUE,
            borderRadius: '0 4px 4px 0',
          }}
        />
      </div>
    </li>
  );
};

const TeamActivity = ({ entries, currentUserId, showCompany, comparisonLabel }) => {
  const groups = groupUsers(entries);
  // One scale across every group so bars stay comparable between managers.
  const max = Math.max(1, ...entries.map((e) => e.count));

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="px-6 py-5 border-b border-gray-700">
        <h2 className="text-lg font-semibold text-white">Submissions by team member</h2>
        <p className="text-sm text-gray-500 mt-1">
          Grouped by supervisor{comparisonLabel ? `; change is ${comparisonLabel}` : ''}.
        </p>
      </div>

      {groups.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-gray-500">No team members yet.</p>
      ) : (
        groups.map((group) => (
          <div key={group.key} className="border-b border-gray-700 last:border-b-0">
            <div className="px-6 py-3 bg-gray-900/40 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="text-sm font-semibold text-white truncate">
                  {group.supervisor ? group.supervisor.email : group.title}
                </span>
                {group.supervisor?.user_id === currentUserId && <YouTag />}
                {group.supervisor && <RoleBadge role={group.supervisor.role} />}
                {showCompany && group.supervisor && <CompanyTag name={group.supervisor.company_name} />}
              </div>
              <span className="text-sm text-gray-400 flex-shrink-0">
                <span className="font-semibold text-white tabular-nums">{formatCount(groupTotal(group))}</span> total
              </span>
            </div>
            {group.members.length === 0 ? (
              <p className="px-6 py-3 text-sm text-gray-500">No inspectors yet.</p>
            ) : (
              <ul className="divide-y divide-gray-700/60">
                {group.members.map((entry) => (
                  <MemberRow
                    key={entry.user_id}
                    entry={entry}
                    max={max}
                    isSelf={entry.user_id === currentUserId}
                    showCompany={showCompany}
                  />
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default TeamActivity;
